/**
 * Export XLSX multi-onglets — Tableau de bord complet Salon
 * Utilisé par le gérant et transmissible à une IA de pilotage
 */
import * as XLSX from 'xlsx'
import type { AppState, Resultats } from '../types'
import { calcRevente } from '../calculs'

// ─── Formatage ───────────────────────────────────────────────────────────────
const e0 = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} €`
const e2 = (n: number) => n.toFixed(2)
const p1 = (n: number) => `${n.toFixed(1)} %`

function addHeader(ws: XLSX.WorkSheet, title: string, subtitle: string) {
  // On retourne simplement les lignes de titre pour les ajouter avant les données
  return [[title], [subtitle], ['']]
}

// ─── Onglet 1 : Tableau de bord synthèse ─────────────────────────────────────
function sheetDashboard(state: AppState, r: Resultats, caReel: number): XLSX.WorkSheet {
  const { salon, parametres: p, gerant } = state
  const modeLabel = p.modeGerant === 'passif' ? 'Gérant passif (équipe couvre tout)' : 'Gérant actif (contribue au CA)'
  const seuilActif = p.modeGerant === 'passif' ? r.caMinimumSansGerantTTC : r.caMinimumTTC
  const seuilLabel = p.modeGerant === 'passif' ? 'Seuil équipe TTC (sans gérant)' : 'Seuil global TTC (avec gérant)'
  const couverture = seuilActif > 0 ? (caReel / seuilActif) * 100 : 0

  const rows: (string | number)[][] = [
    ['TABLEAU DE BORD — SEUIL DE RENTABILITÉ'],
    [`Salon : ${salon.nom || '(non renseigné)'}  |  Ville : ${salon.ville || '—'}  |  Postes : ${salon.nbPostes}  |  Jours/semaine : ${salon.joursOuverture}`],
    [''],
    ['═══════════════════════════════════════════════════════'],
    ['SECTION 1 — INFORMATIONS GÉNÉRALES'],
    ['═══════════════════════════════════════════════════════'],
    ['Indicateur', 'Valeur', 'Note / Interprétation'],
    ['Nom du salon', salon.nom || '—', ''],
    ['Ville', salon.ville || '—', ''],
    ['Nombre de postes', salon.nbPostes, ''],
    ['Jours d\'ouverture / semaine', salon.joursOuverture, ''],
    ['Jours travaillés / mois (paramètre)', p.joursTravailles, 'Utilisé pour calculer le seuil journalier'],
    ['Taux de remplissage agenda (%)', p.tauxRemplissage, 'Pourcentage de l\'agenda réellement occupé'],
    ['Taux TVA (%)', p.tauxTVA, 'Appliqué pour passer du HT au TTC'],
    ['% produits techniques / CA', p.pctProduitsTechUtilises, 'Coût proportionnel au CA — augmente le seuil'],
    ['Objectif bénéfice mensuel (€)', p.objectifBenefice, 'Intégré dans le seuil à atteindre'],
    ['Mode gérant', modeLabel, ''],
    [''],
    ['═══════════════════════════════════════════════════════'],
    ['SECTION 2 — SEUIL DE RENTABILITÉ'],
    ['═══════════════════════════════════════════════════════'],
    ['Indicateur', 'Valeur (€)', 'Note'],
    ['Charges fixes mensuelles HT', Math.round(r.chargesFixesMensuelles), 'Toutes charges hors masse salariale'],
    ['Masse salariale mensuelle HT', Math.round(r.masseSalarialeMensuelle), 'Collaborateurs + Gérant'],
    ['dont : coût gérant', Math.round(gerant.coutTotal), 'Rémunération + charges sociales gérant'],
    ['dont : masse salariale collaborateurs', Math.round(r.masseSalarialeMensuelle - gerant.coutTotal), 'Coût employeur total collabs'],
    ['Objectif bénéfice mensuel', Math.round(p.objectifBenefice), 'Marge souhaitée par le gérant'],
    ['Total charges HT', Math.round(r.totalChargesHT), 'Base de calcul avant TVA'],
    ['Impact produits techniques HT', Math.round(r.coutProduitsTechMensuel / (1 + p.tauxTVA / 100)), 'Coût proportionnel au CA'],
    [''],
    ['→ Seuil HT à atteindre', Math.round(r.caMinimumHT), 'Ce que le salon doit facturer (hors TVA)'],
    ['→ Seuil TTC global (gérant actif)', Math.round(r.caMinimumTTC), 'CA TTC total à encaisser — gérant inclus dans la prod'],
    ['→ Seuil TTC équipe (gérant passif)', Math.round(r.caMinimumSansGerantTTC), 'CA TTC que l\'équipe seule doit couvrir'],
    [''],
    [seuilLabel, Math.round(seuilActif), '← Seuil applicable selon le mode sélectionné'],
    ['CA réel saisi (TTC)', Math.round(caReel), 'Chiffre d\'affaires TTC réalisé ce mois'],
    ['Taux de couverture du seuil (%)', parseFloat(couverture.toFixed(1)), couverture >= 100 ? '✅ Seuil atteint' : couverture >= 80 ? '⚠️ Proche du seuil' : '❌ Seuil non atteint'],
    ['Écart seuil / CA réel (€)', Math.round(caReel - seuilActif), caReel >= seuilActif ? 'Excédent' : 'Déficit à combler'],
    [''],
    ['═══════════════════════════════════════════════════════'],
    ['SECTION 3 — SEUILS TEMPORELS'],
    ['═══════════════════════════════════════════════════════'],
    ['Indicateur', 'Gérant ACTIF', 'Gérant PASSIF'],
    ['Seuil journalier TTC (€/jour)', Math.round(r.seuilQuotidienTTC), Math.round(r.seuilQuotidienSansGerantTTC)],
    ['Seuil horaire TTC (€/heure)', parseFloat(r.seuilHoraireTTC.toFixed(2)), '—'],
    ['Seuil minute TTC (€/minute)', parseFloat(r.seuilMinuteTTC.toFixed(4)), parseFloat(r.seuilMinuteSansGerantTTC.toFixed(4))],
    ['Coût-minute théorique TTC', parseFloat(r.coutMinuteTheoriqueTTC.toFixed(4)), ''],
    ['Coût-minute réel TTC (agenda réel)', parseFloat(r.coutMinuteReelTTC.toFixed(4)), `Agenda rempli à ${p.tauxRemplissage}%`],
    ['Écart coût-minute réel / théorique (%)', parseFloat(r.ecartPct.toFixed(1)), 'Écart dû au taux de remplissage'],
    [''],
    ['Heures productives totales / mois', parseFloat(r.heuresProductivesTotales.toFixed(1)), 'Gérant actif inclus si actif'],
    ['Heures productives équipe seule / mois', parseFloat(r.heuresSansGerant.toFixed(1)), 'Sans les heures du gérant'],
    [''],
    ['═══════════════════════════════════════════════════════'],
    ['ANALYSE & RECOMMANDATIONS'],
    ['═══════════════════════════════════════════════════════'],
    ['Point', 'Analyse'],
    ['Couverture du seuil', couverture >= 100 ? `Le salon couvre son seuil (${couverture.toFixed(1)}%). Bonne santé financière.` : `Déficit de ${e0(seuilActif - caReel)} à combler. Priorité : augmenter le CA ou réduire les charges.`],
    ['Mode gérant', p.modeGerant === 'passif' ? 'Mode passif : l\'équipe porte le coût du gérant. Chaque collaborateur doit générer plus de CA.' : 'Mode actif : le gérant contribue directement au CA. La pression sur l\'équipe est allégée.'],
    ['Coût-minute', r.coutMinuteReelTTC > r.coutMinuteTheoriqueTTC ? `Le taux de remplissage (${p.tauxRemplissage}%) fait monter le coût-minute réel à ${e2(r.coutMinuteReelTTC)} €/min. Augmenter le remplissage réduit ce coût.` : 'Le coût-minute est bien maîtrisé.'],
  ]

  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!cols'] = [{ wch: 42 }, { wch: 22 }, { wch: 55 }]
  return ws
}

// ─── Onglet 2 : Charges détaillées ───────────────────────────────────────────
function sheetCharges(state: AppState, r: Resultats): XLSX.WorkSheet {
  const c = state.charges
  const catTotals = r.chargesParCategorie

  const rows: (string | number)[][] = [
    ['CHARGES DÉTAILLÉES — MENSUEL HT'],
    ['Toutes les charges saisies par poste, regroupées par catégorie'],
    [''],
    ['Catégorie', 'Poste de charge', 'Montant mensuel HT (€)', '% du total charges fixes'],
    // Structure
    ['STRUCTURE', 'Loyer', Math.round(c.loyer), c.loyer > 0 ? parseFloat(((c.loyer / r.chargesFixesMensuelles) * 100).toFixed(1)) : 0],
    ['', 'Charges locatives', Math.round(c.chargesLocatives), 0],
    ['', 'Crédit immobilier', Math.round(c.credit), 0],
    ['', 'Leasing matériel', Math.round(c.leasingMateriel), 0],
    ['', 'CFE', Math.round(c.cfe), 0],
    ['', 'Taxe foncière', Math.round(c.taxeFonciere), 0],
    ['', 'Électricité', Math.round(c.electricite), 0],
    ['', 'Eau', Math.round(c.eau), 0],
    ['', 'Assurance', Math.round(c.assurance), 0],
    ['', 'Entretien & réparations', Math.round(c.entretien), 0],
    ['', 'Alarme', Math.round(c.alarme), 0],
    ['', '→ TOTAL Structure', Math.round(catTotals['Structure'] || 0), parseFloat((((catTotals['Structure'] || 0) / r.chargesFixesMensuelles) * 100).toFixed(1))],
    [''],
    // Déplacements
    ['DÉPLACEMENTS', 'Frais kilométriques', Math.round(c.fraisKilometriques), 0],
    ['', 'Achat véhicule', Math.round(c.achatVehicule), 0],
    ['', 'Leasing véhicule', Math.round(c.leasingVehicule), 0],
    ['', 'Essence', Math.round(c.essence), 0],
    ['', 'Assurance véhicule', Math.round(c.assuranceVehicule), 0],
    ['', 'Transport & achats', Math.round(c.transportAchat), 0],
    ['', 'Autres véhicule', Math.round(c.autresVehicule), 0],
    ['', '→ TOTAL Déplacements', Math.round(catTotals['Déplacements'] || 0), parseFloat((((catTotals['Déplacements'] || 0) / r.chargesFixesMensuelles) * 100).toFixed(1))],
    [''],
    // Métier
    ['MÉTIER', 'Petit matériel', Math.round(c.petitMateriel), 0],
    ['', 'Gros matériel', Math.round(c.grosMateriel), 0],
    ['', 'Produits techniques (charges)', Math.round(c.produitsTechniquesCharges), 0],
    ['', 'Vêtements professionnels', Math.round(c.vetementsProf), 0],
    ['', 'Logiciel coiffure', Math.round(c.logicielCoiffure), 0],
    ['', 'Décoration salon', Math.round(c.decoration), 0],
    ['', 'Autres métier', Math.round(c.autresMetier), 0],
    ['', '→ TOTAL Métier', Math.round(catTotals['Métier'] || 0), parseFloat((((catTotals['Métier'] || 0) / r.chargesFixesMensuelles) * 100).toFixed(1))],
    [''],
    // Admin
    ['ADMINISTRATIF', 'Expert-comptable', Math.round(c.expertComptable), 0],
    ['', 'Frais juridiques', Math.round(c.fraisJuridiques), 0],
    ['', 'Honoraires social', Math.round(c.honorairesSocial), 0],
    ['', 'Frais bancaires', Math.round(c.fraisBancaires), 0],
    ['', 'Frais TPE', Math.round(c.fraisTPE), 0],
    ['', 'Logiciel caisse', Math.round(c.logicielCaisse), 0],
    ['', 'Téléphone fixe', Math.round(c.telephoneFixe), 0],
    ['', 'Téléphone mobile', Math.round(c.telephoneMobile), 0],
    ['', 'Hébergement web', Math.round(c.hebergementWeb), 0],
    ['', 'Fournitures', Math.round(c.fournitures), 0],
    ['', 'Presse', Math.round(c.presse), 0],
    ['', 'Cadeaux clients', Math.round(c.cadeauxClients), 0],
    ['', 'Cotisations groupement', Math.round(c.cotisationsGroupement), 0],
    ['', '→ TOTAL Administratif', Math.round(catTotals['Admin'] || 0), parseFloat((((catTotals['Admin'] || 0) / r.chargesFixesMensuelles) * 100).toFixed(1))],
    [''],
    // Social
    ['SOCIAL SALARIÉ', 'Médecine du travail', Math.round(c.medecine), 0],
    ['', 'Mutuelle salariés', Math.round(c.mutuelleSalaries), 0],
    ['', 'Épargne salariale', Math.round(c.epargneSalariale), 0],
    ['', 'ADSAMS', Math.round(c.adsams), 0],
    ['', 'Taxe apprentissage', Math.round(c.taxeApprentissage), 0],
    ['', 'OPCO', Math.round(c.opco), 0],
    ['', '→ TOTAL Social salarié', Math.round(catTotals['Social sal.'] || 0), parseFloat((((catTotals['Social sal.'] || 0) / r.chargesFixesMensuelles) * 100).toFixed(1))],
    [''],
    // Marketing
    ['MARKETING', 'Coaching', Math.round(c.coaching), 0],
    ['', 'Marketing digital', Math.round(c.marketingDigital), 0],
    ['', 'Communication', Math.round(c.communication), 0],
    ['', 'Formation coiffure', Math.round(c.formationCoiffure), 0],
    ['', 'Formation management', Math.round(c.formationManagement), 0],
    ['', '→ TOTAL Marketing', Math.round(catTotals['Marketing'] || 0), parseFloat((((catTotals['Marketing'] || 0) / r.chargesFixesMensuelles) * 100).toFixed(1))],
    [''],
    // Droits
    ['DROITS', 'SACEM', Math.round(c.sacem), 0],
    ['', 'SPRE', Math.round(c.spre), 0],
    ['', '→ TOTAL Droits', Math.round(catTotals['Droits'] || 0), parseFloat((((catTotals['Droits'] || 0) / r.chargesFixesMensuelles) * 100).toFixed(1))],
    [''],
    // Autres
    ['AUTRES', 'Sous-traitance', Math.round(c.soustraitance), 0],
    ['', 'Frais de réception', Math.round(c.fraisReception), 0],
    ['', 'Autres charges', Math.round(c.autresCharges), 0],
    ['', '→ TOTAL Autres', Math.round(catTotals['Autres'] || 0), parseFloat((((catTotals['Autres'] || 0) / r.chargesFixesMensuelles) * 100).toFixed(1))],
    [''],
    [''],
    ['RÉCAPITULATIF CHARGES FIXES', '', '', ''],
    ['Total charges fixes HT/mois', Math.round(r.chargesFixesMensuelles), '', '100 %'],
    ['Masse salariale HT/mois (collabs + gérant)', Math.round(r.masseSalarialeMensuelle), '', ''],
    ['TOTAL GÉNÉRAL (charges + masse salariale)', Math.round(r.chargesFixesMensuelles + r.masseSalarialeMensuelle), '', ''],
  ]

  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!cols'] = [{ wch: 22 }, { wch: 30 }, { wch: 24 }, { wch: 24 }]
  return ws
}

// ─── Onglet 3 : Gérant ────────────────────────────────────────────────────────
function sheetGerant(state: AppState, r: Resultats): XLSX.WorkSheet {
  const { gerant, parametres: p } = state
  const hProd = gerant.heuresMois * (gerant.tauxEfficacite / 100)
  const modeLabel = p.modeGerant === 'passif' ? 'PASSIF — le gérant ne génère pas de CA' : 'ACTIF — le gérant génère du CA'
  const seuilActif = p.modeGerant === 'passif' ? r.caMinimumSansGerantTTC : r.caMinimumTTC
  const seuilLabel = p.modeGerant === 'passif' ? 'Seuil équipe TTC (gérant passif)' : 'Seuil global TTC (gérant actif)'

  const rows: (string | number)[][] = [
    ['GÉRANT / DIRIGEANT'],
    ['Informations sur la rémunération et le mode de contribution du gérant'],
    [''],
    ['Paramètre', 'Valeur', 'Explication'],
    ['Coût total mensuel du gérant (€)', Math.round(gerant.coutTotal), 'Rémunération + charges sociales (TNS ou salarié)'],
    ['Heures travaillées / mois', gerant.heuresMois, 'Total heures au salon'],
    ['Taux d\'efficacité en cabine (%)', gerant.tauxEfficacite, '% du temps réellement en prestation'],
    ['Heures productives / mois', parseFloat(hProd.toFixed(1)), 'Heures × taux d\'efficacité'],
    [''],
    ['Mode gérant actuel', modeLabel, ''],
    [''],
    ['IMPACT SUR LE SEUIL DE RENTABILITÉ', '', ''],
    ['Indicateur', 'Gérant ACTIF', 'Gérant PASSIF'],
    ['Seuil TTC mensuel (€)', Math.round(r.caMinimumTTC), Math.round(r.caMinimumSansGerantTTC)],
    ['Seuil journalier TTC (€/jour)', Math.round(r.seuilQuotidienTTC), Math.round(r.seuilQuotidienSansGerantTTC)],
    ['Seuil / minute TTC (€/min)', parseFloat(r.seuilMinuteTTC.toFixed(4)), parseFloat(r.seuilMinuteSansGerantTTC.toFixed(4))],
    ['Heures productives comptées', parseFloat(r.heuresProductivesTotales.toFixed(1)), parseFloat(r.heuresSansGerant.toFixed(1))],
    [''],
    [seuilLabel, Math.round(seuilActif), '← MODE ACTUEL'],
    [''],
    ['EXPLICATION DES MODES', '', ''],
    ['Mode', 'Description', 'Conséquence sur l\'équipe'],
    ['✍️ Actif', 'Le gérant réalise des prestations et génère du CA. Ses heures s\'ajoutent au total productif.', 'Le seuil est réparti sur plus d\'heures → coût-minute plus bas → moins de pression sur chaque collab.'],
    ['👑 Passif', 'Le gérant ne fait pas de CA (gestion, admin, commercial). L\'équipe doit couvrir TOUTE la masse salariale dont celle du gérant.', 'Le seuil est identique mais réparti sur moins d\'heures → coût-minute plus élevé pour chaque collab.'],
  ]

  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!cols'] = [{ wch: 38 }, { wch: 20 }, { wch: 65 }]
  return ws
}

// ─── Onglet 4 : Collaborateurs ────────────────────────────────────────────────
function sheetCollaborateurs(state: AppState, r: Resultats): XLSX.WorkSheet {
  const { collaborateurs: collabs, parametres: p } = state
  const seuilActif = p.modeGerant === 'passif' ? r.caMinimumSansGerantTTC : r.caMinimumTTC

  const headerRow = [
    'Prénom', 'Nom', 'Statut',
    'Coût total/mois (€)', 'Heures/mois', 'Taux efficacité (%)', 'H.productives/mois',
    'Seuil individuel TTC (€)', 'Seuil quotidien individuel (€)', 'Coût-minute individuel TTC (€)',
    'Part salariale (%)', 'Quote-part charges fixes (€)', 'Quote-part bénéfice (€)',
    'CA réel saisi (€)', 'Taux couverture seuil (%)', 'Statut couverture',
    'Revente TTC réalisée (€)', 'Coefficient fournisseur', '% prime revente',
  ]

  const rows: (string | number | null)[][] = [
    ['COLLABORATEURS — FICHES DÉTAILLÉES'],
    [`Mode gérant : ${p.modeGerant === 'passif' ? 'Passif' : 'Actif'} | Seuil global applicable : ${Math.round(seuilActif)} €`],
    [''],
    headerRow,
  ]

  collabs.forEach(c => {
    const indiv = r.seuilsIndividuels.find(s => s.id === c.id)
    const hProd = c.heuresMois * (c.tauxEfficacite / 100)
    const caReel = c.caReel ?? 0
    const seuilIndiv = indiv?.seuilTTC ?? 0
    const couverture = seuilIndiv > 0 ? (caReel / seuilIndiv) * 100 : null
    const rv = calcRevente(c)

    rows.push([
      c.prenom,
      c.nom,
      c.statut,
      Math.round(c.coutTotal),
      c.heuresMois,
      c.tauxEfficacite,
      parseFloat(hProd.toFixed(1)),
      Math.round(seuilIndiv),
      Math.round(seuilIndiv / (p.joursTravailles || 22)),
      parseFloat((indiv?.coutMinuteIndivTTC ?? 0).toFixed(4)),
      indiv?.ratioPct ?? 0,
      Math.round(indiv?.quotePartCharges ?? 0),
      Math.round(indiv?.quotePartBenef ?? 0),
      Math.round(caReel),
      couverture !== null ? parseFloat(couverture.toFixed(1)) : null,
      couverture === null ? 'Non saisi' : couverture >= 100 ? '✅ Seuil atteint' : couverture >= 80 ? '⚠️ Proche' : '❌ En dessous',
      Math.round(rv.venteTTC),
      c.coeffRevente ?? 2.2,
      c.primePct ?? 10,
    ])
  })

  // Ligne totaux
  rows.push([''])
  rows.push([
    'TOTAL / MOYENNE', '',  '',
    Math.round(collabs.reduce((s, c) => s + c.coutTotal, 0)),
    Math.round(collabs.reduce((s, c) => s + c.heuresMois, 0)),
    collabs.length > 0 ? parseFloat((collabs.reduce((s, c) => s + c.tauxEfficacite, 0) / collabs.length).toFixed(1)) : 0,
    parseFloat(r.heuresSansGerant.toFixed(1)),
    Math.round(r.seuilsIndividuels.reduce((s, si) => s + si.seuilTTC, 0)),
    '', '', '', '', '',
    Math.round(collabs.reduce((s, c) => s + (c.caReel ?? 0), 0)),
    '', '',
    Math.round(collabs.reduce((s, c) => s + (c.venteMoisTTC ?? 0), 0)),
    '', '',
  ])

  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!cols'] = [
    { wch: 12 }, { wch: 14 }, { wch: 14 },
    { wch: 16 }, { wch: 12 }, { wch: 16 }, { wch: 18 },
    { wch: 20 }, { wch: 22 }, { wch: 22 },
    { wch: 16 }, { wch: 22 }, { wch: 20 },
    { wch: 16 }, { wch: 20 }, { wch: 18 },
    { wch: 20 }, { wch: 18 }, { wch: 14 },
  ]
  return ws
}

// ─── Onglet 5 : Revente produits ──────────────────────────────────────────────
function sheetRevente(state: AppState): XLSX.WorkSheet {
  const { collaborateurs: collabs, parametres: p } = state

  const rows: (string | number)[][] = [
    ['REVENTE PRODUITS — ANALYSE PAR COLLABORATEUR'],
    ['Calcul de la marge et des primes sur la revente de produits'],
    [''],
    [
      'Collaborateur', 'Ventes TTC (€)', 'Ventes HT (€)', 'Achat fournisseur HT (€)',
      'Coeff fournisseur', '% prime', 'Base prime', 'Prime brute (€)',
      'Charges patronales (€)', 'Coût prime pour salon (€)', 'Marge brute (€)', 'Bénéfice net salon (€)'
    ],
  ]

  let totalVenteTTC = 0, totalMarge = 0, totalBenef = 0

  collabs.forEach(c => {
    const rv = calcRevente(c, p.tauxTVA)
    totalVenteTTC += rv.venteTTC
    totalMarge += rv.margeBrute
    totalBenef += rv.beneficeNet
    rows.push([
      `${c.prenom} ${c.nom}`,
      Math.round(rv.venteTTC),
      parseFloat(rv.venteHT.toFixed(2)),
      parseFloat(rv.achatHT.toFixed(2)),
      c.coeffRevente ?? 2.2,
      c.primePct ?? 10,
      rv.primeBaseTTC ? 'TTC' : 'HT',
      parseFloat(rv.primeBrute.toFixed(2)),
      parseFloat(rv.chargesPrime.toFixed(2)),
      parseFloat(rv.coutPrimeSalon.toFixed(2)),
      parseFloat(rv.margeBrute.toFixed(2)),
      parseFloat(rv.beneficeNet.toFixed(2)),
    ])
  })

  rows.push([''])
  rows.push([
    'TOTAL', Math.round(totalVenteTTC), '', '', '', '', '', '', '', '',
    parseFloat(totalMarge.toFixed(2)), parseFloat(totalBenef.toFixed(2)),
  ])
  rows.push([''])
  rows.push(['EXPLICATION DES COLONNES', ''])
  rows.push(['Coeff fournisseur', 'Achat HT = Vente HT ÷ coeff. Ex: coeff 2.2 → marge de 55% sur le HT'])
  rows.push(['Marge brute', 'Vente HT − Achat HT (avant prime et charges)'])
  rows.push(['Bénéfice net salon', 'Marge brute − Prime brute − Charges patronales sur prime'])

  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!cols'] = [
    { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 22 },
    { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 16 },
    { wch: 20 }, { wch: 22 }, { wch: 16 }, { wch: 20 },
  ]
  return ws
}

// ─── Onglet 6 : Données IA ────────────────────────────────────────────────────
function sheetIA(state: AppState, r: Resultats, caReel: number): XLSX.WorkSheet {
  const { salon, parametres: p, gerant, collaborateurs: collabs } = state
  const seuilActif = p.modeGerant === 'passif' ? r.caMinimumSansGerantTTC : r.caMinimumTTC
  const couverture = seuilActif > 0 ? (caReel / seuilActif) * 100 : 0

  // On génère un JSON structuré lisible par une IA
  const payload = {
    meta: {
      date_export: new Date().toISOString().slice(0, 10),
      salon: salon.nom,
      ville: salon.ville,
      nb_postes: salon.nbPostes,
      jours_ouverture_semaine: salon.joursOuverture,
    },
    parametres: {
      taux_remplissage_pct: p.tauxRemplissage,
      pct_produits_tech: p.pctProduitsTechUtilises,
      objectif_benefice_eur: p.objectifBenefice,
      jours_travailles_mois: p.joursTravailles,
      taux_tva_pct: p.tauxTVA,
      mode_gerant: p.modeGerant,
    },
    gerant: {
      cout_total_mensuel_eur: gerant.coutTotal,
      heures_mois: gerant.heuresMois,
      taux_efficacite_pct: gerant.tauxEfficacite,
      heures_productives: Math.round(gerant.heuresMois * gerant.tauxEfficacite / 100 * 10) / 10,
    },
    seuil_rentabilite: {
      charges_fixes_eur: Math.round(r.chargesFixesMensuelles),
      masse_salariale_eur: Math.round(r.masseSalarialeMensuelle),
      total_charges_ht_eur: Math.round(r.totalChargesHT),
      seuil_ht_eur: Math.round(r.caMinimumHT),
      seuil_ttc_gerant_actif_eur: Math.round(r.caMinimumTTC),
      seuil_ttc_gerant_passif_eur: Math.round(r.caMinimumSansGerantTTC),
      seuil_applicable_eur: Math.round(seuilActif),
      ca_reel_ttc_eur: Math.round(caReel),
      taux_couverture_pct: Math.round(couverture * 10) / 10,
      excedent_ou_deficit_eur: Math.round(caReel - seuilActif),
    },
    seuils_temporels: {
      seuil_journalier_actif_eur: Math.round(r.seuilQuotidienTTC),
      seuil_journalier_passif_eur: Math.round(r.seuilQuotidienSansGerantTTC),
      seuil_horaire_eur: Math.round(r.seuilHoraireTTC * 100) / 100,
      cout_minute_theorique_eur: Math.round(r.coutMinuteTheoriqueTTC * 10000) / 10000,
      cout_minute_reel_eur: Math.round(r.coutMinuteReelTTC * 10000) / 10000,
      heures_prod_totales: Math.round(r.heuresProductivesTotales * 10) / 10,
      heures_prod_equipe_seule: Math.round(r.heuresSansGerant * 10) / 10,
    },
    collaborateurs: collabs.map(c => {
      const indiv = r.seuilsIndividuels.find(s => s.id === c.id)
      const rv = calcRevente(c, p.tauxTVA)
      return {
        prenom: c.prenom,
        nom: c.nom,
        statut: c.statut,
        cout_total_eur: Math.round(c.coutTotal),
        heures_mois: c.heuresMois,
        taux_efficacite_pct: c.tauxEfficacite,
        heures_productives: Math.round(c.heuresMois * c.tauxEfficacite / 100 * 10) / 10,
        seuil_individuel_ttc_eur: Math.round(indiv?.seuilTTC ?? 0),
        cout_minute_individuel_eur: Math.round((indiv?.coutMinuteIndivTTC ?? 0) * 10000) / 10000,
        ca_reel_ttc_eur: Math.round(c.caReel ?? 0),
        taux_couverture_seuil_pct: indiv?.seuilTTC && c.caReel ? Math.round(((c.caReel / indiv.seuilTTC) * 100) * 10) / 10 : null,
        revente_ttc_eur: Math.round(rv.venteTTC),
        marge_revente_eur: Math.round(rv.margeBrute),
        prime_revente_eur: Math.round(rv.primeBrute),
        benefice_net_revente_eur: Math.round(rv.beneficeNet),
      }
    }),
    charges_par_categorie: Object.fromEntries(
      Object.entries(r.chargesParCategorie).map(([k, v]) => [k, Math.round(v)])
    ),
  }

  const jsonStr = JSON.stringify(payload, null, 2)
  const rows: (string)[][] = [
    ['DONNÉES STRUCTURÉES POUR IA — NE PAS MODIFIER'],
    ['Ce fichier JSON peut être copié et collé directement dans une IA (ChatGPT, Claude, etc.) pour obtenir une analyse de pilotage du salon.'],
    [''],
    ['INSTRUCTION SUGGÉRÉE POUR L\'IA :'],
    ['Voici les données financières de mon salon de coiffure. Analyse-les et dis-moi :'],
    ['1. Est-ce que je couvre mon seuil de rentabilité ? Quel est l\'écart ?'],
    ['2. Quels collaborateurs sont en dessous de leur seuil individuel ?'],
    ['3. Quelles charges sont les plus importantes et lesquelles pourrais-je réduire ?'],
    ['4. Mon mode gérant (actif/passif) est-il adapté à ma situation ?'],
    ['5. Quels sont mes principaux leviers d\'amélioration ce mois-ci ?'],
    [''],
    ['JSON À COPIER :'],
    [jsonStr],
  ]

  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!cols'] = [{ wch: 120 }]
  return ws
}

// ─── Fonction principale d'export ────────────────────────────────────────────
export function exportTableauDeBord(state: AppState, r: Resultats, caReel: number) {
  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(wb, sheetDashboard(state, r, caReel), '📊 Tableau de bord')
  XLSX.utils.book_append_sheet(wb, sheetCharges(state, r),           '💰 Charges')
  XLSX.utils.book_append_sheet(wb, sheetGerant(state, r),            '👑 Gérant')
  XLSX.utils.book_append_sheet(wb, sheetCollaborateurs(state, r),    '👥 Collaborateurs')
  XLSX.utils.book_append_sheet(wb, sheetRevente(state),              '🔁 Revente')
  XLSX.utils.book_append_sheet(wb, sheetIA(state, r, caReel),        '🤖 Données IA')

  const nomSalon = (state.salon.nom || 'salon').replace(/[^a-zA-Z0-9_-]/g, '_')
  const date = new Date().toISOString().slice(0, 10)
  XLSX.writeFile(wb, `Tableau_de_bord_${nomSalon}_${date}.xlsx`)
}
